import { subDays, format } from 'date-fns'
import { useStore, useStoreMap } from 'effector-react'
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons'
import React, { useEffect, useMemo, useState } from 'react'
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import { balanceHistoriesModel } from '~/entities/BalanceHistories'
import { CURRENCY } from '~/shared/config/constants'
import { formatDateForApi } from '~/shared/lib/date'
import { ProgressBlock } from '~/shared/ui/Loaders'

import { Wrapper, MainBlock, IconButton } from './staled'

const currentDate = new Date()

const createDate = (subDaysCount: number) => {
  return format(subDays(currentDate, subDaysCount), 'd MMMM. yyyy г')
}

const uiCurrentDate = createDate(0)

type BalanceCalendarHeadingProps = {
  onDateChange: (date: string) => void
}

export function BalanceCalendarHeading({
  onDateChange,
}: BalanceCalendarHeadingProps) {
  const [subDaysCount, setSubDaysCount] = useState<number>(0)
  const [movement, setMovement] = useState<string>('back')
  const ref = React.useRef(null)

  const isLoading = useStore(
    balanceHistoriesModel.getBalanceReportForDayFx.pending,
  )

  const profitAmount = useStoreMap(
    balanceHistoriesModel.$balanceReportForDay,
    (balanceReport) => balanceReport?.getProfitAmount() || 0,
  )
  const uiDate = useMemo(() => createDate(subDaysCount), [subDaysCount])

  const uiDateForApi = useMemo(
    () => formatDateForApi(subDays(currentDate, subDaysCount)),
    [subDaysCount],
  )
  const isDisabledButtonNext = uiDate === uiCurrentDate

  useEffect(() => {
    onDateChange(uiDateForApi)
  }, [onDateChange, uiDateForApi])

  const handleBackMovement = () => {
    setMovement('back')
    const timeout = setTimeout(() => {
      setSubDaysCount((prev) => prev + 1)
      clearTimeout(timeout)
    }, 0)
  }
  const handleNextMovement = () => {
    setMovement('next')
    const timeout = setTimeout(() => {
      setSubDaysCount((prev) => prev - 1)
      clearTimeout(timeout)
    }, 0)
  }

  return (
    <Wrapper>
      <IconButton
        onClick={handleBackMovement}
        variant='outline'
        icon={chevronBackOutline}
        iconProps={{ size: '10px' }}
      />

      <MainBlock>
        <SwitchTransition mode='out-in'>
          <CSSTransition
            key={uiDate}
            nodeRef={ref}
            timeout={200}
            unmountOnExit
            classNames={`fade-${movement}`}
          >
            <div key={uiDate} ref={ref}>
              <div style={{ marginBottom: '24px', fontSize: '16px' }}>
                {uiDate}
              </div>

              {isLoading ? (
                <ProgressBlock height={41} size={24} />
              ) : (
                <div
                  style={{ fontSize: '32px', fontWeight: 500 }}
                  data-testid='balance-calendar-profit-amount'
                >
                  {profitAmount.toLocaleString()} {CURRENCY}
                </div>
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>

        <div style={{ fontSize: '12px' }}>Заработок за день</div>
      </MainBlock>

      <IconButton
        onClick={handleNextMovement}
        disabled={isDisabledButtonNext}
        variant='outline'
        icon={chevronForwardOutline}
      />
    </Wrapper>
  )
}
